import { createClient } from './http';
import type { AdminBasemap, BasemapSourceType, WmsBasemapSettings, WmtsBasemapSettings } from '../admin/types';
import type { OgcConnectionInput } from './layersApi';

const MAPS_API_BASE = import.meta.env.VITE_MAPS_API_BASE_URL ?? 'https://x-gis.xenus.dev/api/maps';

const request = createClient(MAPS_API_BASE);

type BasemapDto = {
  id: string;
  name: string;
  source_type: string;
  url_template: string;
  min_zoom: number;
  max_zoom: number;
  tile_size: number;
  crs: string;
  attribution: string;
  api_key: string | null;
  enabled: boolean;
  is_default: boolean;
  sort_order: number;
  settings: WmtsBasemapSettings | WmsBasemapSettings | Record<string, never>;
};

const sourceTypeToDto: Record<BasemapSourceType, string> = {
  'XYZ Tiles': 'xyz',
  WMTS: 'wmts',
  TMS: 'tms',
  MBTiles: 'mbtiles',
  WMS: 'wms',
};

const sourceTypeFromDto: Record<string, BasemapSourceType> = {
  xyz: 'XYZ Tiles',
  wmts: 'WMTS',
  tms: 'TMS',
  mbtiles: 'MBTiles',
  wms: 'WMS',
};

function toPayload(basemap: AdminBasemap) {
  return {
    name: basemap.name,
    source_type: sourceTypeToDto[basemap.sourceType],
    url_template: basemap.urlTemplate,
    min_zoom: basemap.minZoom,
    max_zoom: basemap.maxZoom,
    tile_size: basemap.tileSize,
    crs: basemap.crs,
    attribution: basemap.attribution,
    api_key: basemap.apiKey || null,
    enabled: basemap.enabled,
    is_default: basemap.isDefault,
    settings: basemap.settings ?? {},
  };
}

type BasemapPresetDto = {
  key: string;
  name: string;
  source_type: string;
  url_template: string;
  min_zoom: number;
  max_zoom: number;
  tile_size: number;
  crs: string;
  attribution: string;
  note: string | null;
  settings: WmsBasemapSettings | Record<string, never>;
};

export type BasemapPreset = {
  key: string;
  name: string;
  sourceType: BasemapSourceType;
  urlTemplate: string;
  minZoom: number;
  maxZoom: number;
  tileSize: 256 | 512;
  crs: string;
  attribution: string;
  note: string | null;
  settings?: WmsBasemapSettings;
};

function presetFromDto(dto: BasemapPresetDto): BasemapPreset {
  return {
    key: dto.key,
    name: dto.name,
    sourceType: sourceTypeFromDto[dto.source_type] ?? 'XYZ Tiles',
    urlTemplate: dto.url_template,
    minZoom: dto.min_zoom,
    maxZoom: dto.max_zoom,
    tileSize: dto.tile_size === 512 ? 512 : 256,
    crs: dto.crs,
    attribution: dto.attribution,
    note: dto.note,
    settings: dto.settings && 'layer' in dto.settings ? (dto.settings as WmsBasemapSettings) : undefined,
  };
}

function fromDto(dto: BasemapDto): AdminBasemap {
  return {
    id: dto.id,
    name: dto.name,
    sourceType: sourceTypeFromDto[dto.source_type] ?? 'XYZ Tiles',
    urlTemplate: dto.url_template,
    minZoom: dto.min_zoom,
    maxZoom: dto.max_zoom,
    tileSize: dto.tile_size === 512 ? 512 : 256,
    crs: dto.crs,
    attribution: dto.attribution,
    apiKey: dto.api_key ?? undefined,
    enabled: dto.enabled,
    isDefault: dto.is_default,
    settings:
      dto.settings && 'matrix_ids' in dto.settings
        ? (dto.settings as WmtsBasemapSettings)
        : dto.settings && 'layer' in dto.settings
          ? (dto.settings as WmsBasemapSettings)
          : undefined,
  };
}

export const basemapsApi = {
  listBasemaps: async (): Promise<AdminBasemap[]> => {
    const rows = await request<BasemapDto[]>('/basemaps', {}, true);
    return rows.map(fromDto);
  },

  listPresets: async (): Promise<BasemapPreset[]> => {
    const rows = await request<BasemapPresetDto[]>('/basemaps/presets', {}, true);
    return rows.map(presetFromDto);
  },

  createBasemap: async (basemap: AdminBasemap): Promise<AdminBasemap> => {
    const dto = await request<BasemapDto>('/basemaps', { method: 'POST', body: JSON.stringify(toPayload(basemap)) }, true);
    return fromDto(dto);
  },

  updateBasemap: async (id: string, basemap: AdminBasemap): Promise<AdminBasemap> => {
    const dto = await request<BasemapDto>(`/basemaps/${id}`, { method: 'PATCH', body: JSON.stringify(toPayload(basemap)) }, true);
    return fromDto(dto);
  },

  deleteBasemap: async (id: string): Promise<void> => {
    await request<void>(`/basemaps/${id}`, { method: 'DELETE' }, true);
  },

  /** Resolves the WMTS layer's tile matrix set from GetCapabilities once, server-side,
   * and publishes it as a basemap — anonymous servers only (see backend note). */
  commitWmtsBasemap: async (body: {
    connection: OgcConnectionInput;
    layer_name: string;
    name: string;
    is_default: boolean;
  }): Promise<AdminBasemap> => {
    const dto = await request<BasemapDto>(
      '/basemaps/wmts/commit',
      { method: 'POST', body: JSON.stringify(body) },
      true,
    );
    return fromDto(dto);
  },
};
